import { Target, XCircle, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function CalibrationDialog({ breakdown, actuals, setActuals, saving, error, success, onClose, onSubmit }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm" data-testid="calibration-dialog">
      <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
              <Target size={16} className="text-amber-400"/>
            </div>
            <div>
              <div className="font-display font-semibold text-slate-100">{t("calibrationDlg.title")}</div>
              <div className="text-xs text-slate-500">{t("calibrationDlg.subtitle")}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-100" data-testid="calibration-close">
            <XCircle size={20}/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {success ? (
            <div className="space-y-4" data-testid="calibration-success">
              <div className="px-4 py-3 rounded-md bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 flex items-start gap-3">
                <CheckCircle2 size={18} className="mt-0.5"/>
                <div>
                  <div className="font-medium">{t("calibrationDlg.appliedTitle")}</div>
                  <div className="text-xs text-emerald-200/80 mt-1">{t("calibrationDlg.appliedBody")}</div>
                </div>
              </div>
              {success.length > 0 && (
                <div className="tech-card overflow-hidden">
                  <div className="px-4 py-2 border-b border-slate-800 label-tech">{t("calibrationDlg.updatedFactors")}</div>
                  <div className="divide-y divide-slate-800">
                    {success.map((u, i) => (
                      <div key={`u-${i}`} className="px-4 py-2 flex items-center justify-between text-sm">
                        <div className="text-slate-300 font-mono-data text-xs">{u.machine_id}</div>
                        <div className="flex items-center gap-3">
                          <span className="text-blue-400 font-mono-data">×{u.factor}</span>
                          <span className="text-[10px] text-slate-500">{t("calibrationDlg.samples", { count: u.sample_count })}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="text-sm text-slate-400 mb-4 leading-relaxed">
                {t("calibrationDlg.intro")}
              </div>
              <div className="space-y-2">
                {breakdown.map((b, i) => (
                  <div key={`cal-${i}`} className="flex items-center gap-3 px-3 py-2 rounded bg-slate-950 border border-slate-800" data-testid={`calib-row-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-200">{b.operation_label}</div>
                      <div className="text-[10px] text-slate-500 truncate">{b.machine_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] text-slate-500 mb-0.5">AI: {(b.minutes_per_piece || 0).toFixed(2)}&apos;</div>
                      <input
                        type="number" step="0.1" min="0"
                        value={actuals[i] ?? ""}
                        onChange={(e) => setActuals({ ...actuals, [i]: e.target.value })}
                        className="w-24 px-2 py-1 rounded text-sm font-mono-data text-right"
                        data-testid={`calib-input-${i}`}
                      />
                      <span className="ml-1 text-xs text-slate-500">{t("calibrationDlg.minPerPc")}</span>
                    </div>
                  </div>
                ))}
              </div>
              {error && <div className="mt-3 text-sm text-red-400" data-testid="calibration-error">{error}</div>}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-800 flex items-center justify-end gap-2">
          {success ? (
            <button onClick={onClose} className="btn-primary px-5 py-2 rounded-md text-sm" data-testid="calibration-done">{t("calibrationDlg.done")}</button>
          ) : (
            <>
              <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-slate-300 bg-slate-800 border border-slate-700 hover:border-slate-500" data-testid="calibration-cancel">{t("common.cancel")}</button>
              <button onClick={onSubmit} disabled={saving} className="btn-primary px-5 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-50" data-testid="calibration-submit">
                <Target size={14}/> {saving ? t("common.saving") : t("calibrationDlg.save")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
